import React from 'react';
import { UserMessage } from './UserMessage';
import { QuestionsMessage } from './QuestionsMessage';
import { AssistantMessage } from './AssistantMessage';
import { ErrorBoundary } from '../../../../shared/components/error/ErrorBoundary';
import { useErrorHandler } from '../../../../shared/hooks/useErrorHandler';

interface MessageProps {
  content: string;
  role: string;
  isStreaming?: boolean;
  isSharedView?: boolean;
  hasError?: boolean;
  onRetry?: () => void;
  onTellMeMore: (verse: { book: string; chapter: string; verse: string }) => void;
  onFollowUpQuestion: (question: string) => void;
  isFirst?: boolean;
  hideFollowUpQuestions?: boolean;
}

export const Message = React.memo(function Message({
  content,
  role,
  isStreaming = false,
  isSharedView = false,
  hasError = false,
  onRetry,
  onTellMeMore,
  onFollowUpQuestion,
  isFirst = false,
  hideFollowUpQuestions = false,
}: MessageProps) {
  const { handleError } = useErrorHandler();

  const renderMessage = () => {
    try {
      switch (role) {
        case 'user':
          return <UserMessage content={content} isFirst={isFirst} />;
        case 'questions':
          return hideFollowUpQuestions ? null : (
            <QuestionsMessage 
              content={content} 
              onFollowUpQuestion={onFollowUpQuestion} 
            />
          );
        case 'assistant':
          return (
            <AssistantMessage
              content={content}
              isStreaming={isStreaming}
              hasError={hasError}
              onRetry={onRetry}
              onTellMeMore={onTellMeMore}
            />
          );
        default:
          throw new Error(`Unknown message role: ${role}`);
      }
    } catch (error) {
      handleError(error, {
        severity: 'medium',
        userMessage: 'Unable to display message',
        context: { role, content }
      });
      return null;
    }
  };

  return (
    <ErrorBoundary
      fallback={({ error }) => (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600 text-sm">Failed to display message</p>
        </div>
      )}
    >
      {renderMessage()}
    </ErrorBoundary>
  );
});